/* eslint-disable promise/prefer-await-to-then */
import type {Callback1, Pointer, Preset, PS, ValueOf, Panel} from '@wix/document-services-types'
import experiment from 'experiment-amd'
import _ from 'lodash'
import type {PreBuildAppResponse, WidgetDevCenterInfo} from '@wix/ambassador-blocks-v1-blocks-app/types'
import DSErrors from '../errors/errors'
import page from '../page/page'
import responsiveLayout from '../responsiveLayout/responsiveLayout'
import saveRunner from '../saveAPI/lib/saveRunner'
import saveState from '../saveAPI/lib/saveState'
import utils from '../utils/utils'
import variants from '../variants/variants'
import appStudioDataModel from './appStudioDataModel'
import appStudioPanels from './appStudioPanels'
import appStudioPresets from './appStudioPresets'
import manifestUtils from './manifestUtils'
import wixBlocksService, {UNKNOWN_ERROR_CODE} from './services/wixBlocksService'
import controllerConfig from './controllerConfig'
import widgetDescriptors from './widgetDescriptors'
import widgetSlotPlaceholders from './widgetSlotPlaceholders'
import appStudioDependencies from './appStudioDependencies'

const {INVALID_VERSION_TYPE, NO_APP_DEF_ID, NO_BUILD_ID, NO_PACKAGE_IMPORT_NAME} = DSErrors.build

const {SAVE_DISABLED_IN_DOCUMENT_SERVICES} = DSErrors.save

const setAppWidgetSettings = (
    ps: PS,
    widgetPageId: string,
    devCenterInfo: WidgetDevCenterInfo,
    isVariation?: boolean
) => {
    const pagePointer = page.getPage(ps, widgetPageId)
    const appWidget = appStudioDataModel.getRootAppWidgetByPage(ps, pagePointer)
    controllerConfig.update(ps, appWidget, devCenterInfo)
    if (isVariation) {
        controllerConfig.update(ps, appWidget, {variationPageId: widgetPageId})
    }
}

const setWidgetRootsSettings = (ps: PS, widget, widgetPageId: string, devCenterInfo: WidgetDevCenterInfo) => {
    setAppWidgetSettings(ps, widgetPageId, devCenterInfo)
    const serializedWidget = appStudioDataModel.serializeWidget(ps, widget.pointer)

    _.forEach(serializedWidget.variations, variation => {
        setAppWidgetSettings(ps, utils.stripHashIfExists(variation.rootCompId), devCenterInfo, true)
    })
}

const setDevCenterWidgetInfo = (ps: PS, devCenterInfoMap: PreBuildAppResponse['devCenterWidgetInfoMap']) => {
    _.forEach(devCenterInfoMap, (devCenterInfo, widgetPageId) => {
        const widget = appStudioDataModel.findWidgetByPageId(ps, widgetPageId)
        appStudioDataModel.setDevCenterInfo(ps, widget.pointer, devCenterInfo)
        setWidgetRootsSettings(ps, widget, widgetPageId, devCenterInfo)
    })
}

const updatePresetsDefaultSize = (ps: PS) => {
    const widgets = appStudioDataModel.getAllWidgets(ps)
    widgets.forEach(({pointer: widgetPointer}) => {
        const rootContainer = appStudioDataModel.getRootContainerByWidgetPointer(ps, widgetPointer)

        const layout = responsiveLayout.get(ps, rootContainer)

        const widgetMinHeight = _.get(
            layout,
            ['componentLayouts', 0, 'minHeight', 'value'],
            _.get(layout, ['componentLayout', 'minHeight', 'value'])
        )

        const presets = appStudioPresets.getWidgetPresets(ps, widgetPointer)

        presets.forEach(({pointer: presetPointer}) => {
            const defaultSize = appStudioPresets.getPresetDefaultSize(ps, presetPointer) || {}

            const vp = appStudioPresets.getPresetVariantPointer(ps, presetPointer, widgetPointer)
            const cvp = variants.getPointerWithVariants(ps, rootContainer, vp)

            const presetStageContainerLayout = responsiveLayout.get(ps, cvp)
            const presetMinHeight = _.get(
                presetStageContainerLayout,
                ['componentLayout', 'minHeight', 'value'],
                widgetMinHeight
            )

            const newSize = _.defaultsDeep({height: {value: presetMinHeight, type: 'px'}}, defaultSize)
            appStudioPresets.setPresetDefaultSize(ps, presetPointer, newSize)
        })
    })
}

const getExistingWidgets = (ps: PS) => {
    const widgets = appStudioDataModel.getAllSerializedWidgets(ps)
    return widgets.map(widget => {
        const widgetInfo = {
            name: widget.name,
            devCenterWidgetId: widget.devCenterWidgetId,
            devCenterEditorReactComponentId: widget.devCenterEditorReactComponentId,
            id: utils.stripHashIfExists(widget.rootCompId),
            selector: undefined
        }
        if (experiment.isOpen('dm_addWidgetSelector')) {
            const widgetPageId = utils.stripHashIfExists(widget.rootCompId)
            const pagePointer = page.getPage(ps, widgetPageId)
            const rootAppWidgetPointer = appStudioDataModel.getRootAppWidgetByPage(ps, pagePointer)
            widgetInfo.selector = `[id$="${rootAppWidgetPointer.id}"]`
        }
        return widgetInfo
    })
}

const preBuild = async (ps: PS, appDefinitionId: string, {onSuccess = _.noop, onError = _.noop} = {}) => {
    appStudioDataModel.updateWidgetContainedWidgets(ps)
    updatePresetsDefaultSize(ps)
    appStudioPanels.updatePanelsRootData(ps)

    await wixBlocksService
        .preBuild(ps, appDefinitionId, getExistingWidgets(ps))
        .then(({devCenterWidgetInfoMap}) => {
            setDevCenterWidgetInfo(ps, devCenterWidgetInfoMap)
        })
        .then(() => {
            onSuccess()
        })
        .catch(onError)
}

const VERSION_TYPES = {
    minor: 'minor',
    major: 'major',
    build: 'build',
    test: 'test'
} as const

export type VersionType = ValueOf<typeof VERSION_TYPES>

function isVersionTypeValid(versionType: VersionType) {
    return !!VERSION_TYPES[versionType]
}

function convertPresetInfoToMatchProtoDefinition(ps: PS, presetPointer: Pointer) {
    const presetName = appStudioPresets.getPresetName(ps, presetPointer)
    const presetId = appStudioPresets.getPresetVariantId(ps, presetPointer)
    const defaultSize: Preset['defaultSize'] = appStudioPresets.getPresetDefaultSize(ps, presetPointer)

    return {
        name: presetName,
        presetId,
        defaultSize: {
            width: defaultSize?.width && {
                value: Math.round(defaultSize.width.value),
                type: defaultSize.width.type.toUpperCase()
            },
            height: defaultSize?.height && {
                value: Math.round(defaultSize.height.value),
                type: defaultSize.height.type.toUpperCase()
            }
        }
    }
}

const convertPanelInfoToMatchProtoDefinition = (panel: Panel) => ({
    pageId: utils.stripHashIfExists(panel.rootCompId),
    name: panel.name,
    height: panel.height,
    title: panel.title,
    pageUrl: panel.pageUrl
})

function getWidgetsMapForBuild(ps: PS) {
    if (experiment.isOpen('dm_useBlocksExtensionGetWidgetsMapForBuild')) {
        return ps.extensionAPI.blocks.getWidgetsMapForBuild()
    }
    const allWidgets = appStudioDataModel.getAllWidgets(ps)
    const containingWidgetsMap = _.mapValues(appStudioDataModel.getContainingWidgetsMap(ps), (widgetIds: string[]) =>
        widgetIds.map(widgetId => {
            const pointer = appStudioDataModel.getDescriptorPointerById(ps, widgetId)
            return appStudioDataModel.getWidgetDevCenterId(ps, pointer)
        })
    )

    return _.reduce(
        allWidgets,
        (accumulator, {pointer, variations, name, plugin}) => {
            const widgetRootCompId = appStudioDataModel.getRootCompIdByPointer(ps, pointer)
            const variationRootCompIds = _.map(variations, variationId => {
                const variationPointer = ps.pointers.data.getDataItemFromMaster(_.replace(variationId, '#', ''))
                return appStudioDataModel.getRootCompIdByPointer(ps, variationPointer)
            })
            const manifestInfo = manifestUtils.getWidgetManifest(ps, pointer)
            const widgetPublicDescriptor = widgetDescriptors.createPublicDescriptor(ps, pointer)
            const presets = appStudioPresets.getWidgetPresets(ps, pointer)
            const presetsInfo = presets.map(preset => convertPresetInfoToMatchProtoDefinition(ps, preset.pointer))
            const panels = appStudioDataModel.getAllPanels(ps)
            const panelsInfo = panels.map(panel => convertPanelInfoToMatchProtoDefinition(panel))
            const widgetData = appStudioDataModel.getData(ps, pointer)

            accumulator[widgetRootCompId] = {
                widgetId: widgetRootCompId,
                variations: variationRootCompIds,
                manifestInfo,
                widgetPublicDescriptor,
                name,
                nestedWidgetsIds: containingWidgetsMap[pointer.id] ?? [],
                panelsInfo,
                devCenterInfo: {
                    devCenterWidgetId: widgetData.devCenterWidgetId,
                    devCenterEditorReactComponentId: widgetData.devCenterEditorReactComponentId
                }
            }

            if (experiment.isOpen('dm_sendPresetInfoInBlocksBuild')) {
                accumulator[widgetRootCompId].presetsInfo = presetsInfo
            }

            if (experiment.isOpen('dm_addWidgetSelector')) {
                const rootAppWidgetPointer = appStudioDataModel.getAppWidgetRefFromPointer(ps, pointer)
                accumulator[widgetRootCompId].selector = `[id$="${rootAppWidgetPointer.id}"]`
            }

            accumulator[widgetRootCompId].plugin = plugin

            if (experiment.isOpen('dm_addSiteWidgetSlotExtensions')) {
                const slotPlaceholders = widgetSlotPlaceholders.getSlotPlaceholdersDataByPointer(ps, pointer)

                if (slotPlaceholders.length > 0) {
                    accumulator[widgetRootCompId].slotPlaceholders = slotPlaceholders
                }
            }

            return accumulator
        },
        {}
    )
}

function getCodePackageData(ps: PS, packageImportName: string) {
    const codePackageData: any = _.head(appStudioDataModel.getAppStudioData(ps)?.codePackages)
    if (!codePackageData) {
        return undefined
    }

    if (!packageImportName) {
        const error = wixBlocksService.generateBuildError({
            errorCode: UNKNOWN_ERROR_CODE,
            errorDescription: 'packageImportName must being passed',
            errorType: NO_PACKAGE_IMPORT_NAME
        })
        throw error
    }

    return {
        packageDisplayName: codePackageData.displayName,
        packageImportName,
        description: codePackageData.description
    }
}

function updateLastBuildId(ps: PS, response) {
    const pointer = ps.pointers.appBuilder.getLastBuildIdPointer()
    ps.dal.set(pointer, response.buildRecord?.id)
}

async function performBuild(ps: PS, options): Promise<any> {
    const {
        versionType,
        appMarketingName,
        blocksVersion,
        appDefId,
        isAsyncBuild,
        releaseNotes,
        publishRc,
        packageImportName
    } = options

    if (!saveState.canSave(ps)) {
        throw wixBlocksService.generateBuildError({
            errorCode: UNKNOWN_ERROR_CODE,
            errorDescription: 'DocumentServices was created with parameter disableSave=true, so build is disabled.',
            errorType: SAVE_DISABLED_IN_DOCUMENT_SERVICES
        })
    }
    const widgetsMap = getWidgetsMapForBuild(ps)
    if (isAsyncBuild) {
        const codePackage = getCodePackageData(ps, packageImportName)
        const response = await wixBlocksService.asyncBuild(ps, appDefId, {
            widgetsMap,
            appMarketingName,
            blocksVersion,
            versionType,
            codePackage,
            releaseNotes,
            publishRc,
            dependencies: appStudioDependencies.getAllDependencies(ps)
        })

        if (!response.buildRecord?.status) {
            throw wixBlocksService.generateBuildError({
                errorCode: UNKNOWN_ERROR_CODE,
                errorDescription: 'Build status required for async build'
            })
        }
        updateLastBuildId(ps, response)
        return response
    }
    const response = await wixBlocksService.build(ps, appDefId, {
        widgetsMap,
        appMarketingName,
        blocksVersion,
        versionType,
        publishRc
    })

    if (!response.version || !response.revision) {
        throw wixBlocksService.generateBuildError({
            errorCode: UNKNOWN_ERROR_CODE,
            errorDescription: 'Build version and revision required for build'
        })
    }
    return response
}

function buildWithOptions(
    ps: PS,
    options: {
        onSuccess?: Callback1<any>
        onError?: Callback1<any>
        versionType: VersionType
        appDefId: string
        appMarketingName?: string
        blocksVersion?: string
        isAsyncBuild?: boolean
        releaseNotes?
        publishRc?: boolean
    }
) {
    const {onSuccess, onError, versionType, appDefId} = options

    if (!appDefId) {
        onError(
            wixBlocksService.generateBuildError({
                errorCode: UNKNOWN_ERROR_CODE,
                errorDescription: 'appDefId must being passed',
                errorType: NO_APP_DEF_ID
            })
        )
        return
    }

    if (isVersionTypeValid(versionType)) {
        saveRunner.runFunctionInSaveQueue(_.partial(performBuild, ps, options)).then(onSuccess, onError)
    } else {
        onError(
            wixBlocksService.generateBuildError({
                errorCode: UNKNOWN_ERROR_CODE,
                errorDescription: 'Version type should be one of: "build", "minor" "major".',
                errorType: INVALID_VERSION_TYPE
            })
        )
    }
}

function build(
    ps: PS,
    onSuccess,
    onError,
    versionType: VersionType,
    appMarketingName: string,
    blocksVersion = '0.0.1',
    appDefId?: string,
    isAsyncBuild?: boolean,
    releaseNotes?,
    publishRc?: boolean
) {
    const options = {
        onSuccess,
        onError,
        versionType,
        appMarketingName,
        blocksVersion,
        appDefId,
        isAsyncBuild,
        releaseNotes,
        publishRc
    }

    buildWithOptions(ps, options)
}

async function getBuildStatusById(ps: PS, buildId: string, appDefinitionId: string) {
    if (!buildId) {
        throw wixBlocksService.generateBuildError({
            errorCode: UNKNOWN_ERROR_CODE,
            errorDescription: 'buildId must being passed',
            errorType: NO_BUILD_ID
        })
    }

    if (!appDefinitionId) {
        throw wixBlocksService.generateBuildError({
            errorCode: UNKNOWN_ERROR_CODE,
            errorDescription: 'appDefId must being passed',
            errorType: NO_APP_DEF_ID
        })
    }

    return saveRunner.runFunctionInSaveQueue(async () => {
        const buildRecord = await wixBlocksService.getBuildRecordById(ps, buildId, appDefinitionId)
        if (!buildRecord?.status) {
            throw wixBlocksService.generateBuildError({
                errorCode: UNKNOWN_ERROR_CODE,
                errorDescription: 'Build Record has missing "status" field'
            })
        }
        return buildRecord
    })
}

export default {
    preBuild,
    buildWithOptions,
    build,
    getBuildStatusById
}
